<template>
  <div class="">
    <!-- Header and Add Button -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">List of event categories</h2>
      </div>
      <button
        @click="addingNewItem"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add Category
      </button>
    </div>
    <!-- Filter Bar -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div class="flex bg-gray-200 rounded-lg">
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '0' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '0';
            searchData();
          "
        >
          All
        </button>
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '1' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '1';
            searchData();
          "
        >
          Active
        </button>
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '2' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '2';
            searchData();
          "
        >
          Inactive
        </button>
      </div>
      <!--  Search and Sort Controls -->
      <div class="flex items-center gap-2">
        <vs-select v-model="search.language" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="" text="Language" disabled />
          <vs-select-item value="all" text="All Langs" />
          <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
        </vs-select>
        <button
          @click="resetData()"
          class="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span> Reset
        </button>
      </div>
    </div>
    <!-- Table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3"></th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Short Name</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Language</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Status</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cat, idx) in all" :key="cat._id" class="border-b hover:bg-gray-100 transition">
            <td class="px-4 py-1">{{ idx + 1 }}</td>
            <td class="">
              <img :src="cat.image || require('@/assets/images/No-image.png')" class="rounded-md object-cover" style="width: 90px; height: 60px" />
            </td>
            <td class="px-4 py-1 font-medium text-gray-800">{{ cat.categoryName }}</td>
            <td class="px-4 py-1 font-medium text-gray-800">
              {{ cat.shortName && cat.shortName.length > 30 ? cat.shortName.slice(0, 30) + "..." : cat.shortName }}
            </td>
            <td class="px-4 py-1 text-center">
              <span class="text-gray-500 text-lg">{{ cat.language }}</span>
            </td>
            <td class="px-4 py-1 text-center">
              <span
                :class="cat.status ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'"
                class="inline-block px-3 py-1 rounded-full font-semibold"
              >
                {{ cat.status ? "Active" : "Inactive" }}
              </span>
            </td>
            <td class="px-4 py-1">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="editingItem(cat)"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      itemToDelete = cat;
                      deleteCategoryPopup = true;
                    "
                  >
                    <vs-icon icon="delete_sweep" /> Delete
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
          <tr v-if="!all || all.length === 0">
            <td colspan="12" class="text-center h-full py-6 text-gray-400">No categories found.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Delete Confirmation Popup -->
    <vs-popup title="Delete Category" :active.sync="deleteCategoryPopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this category?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="deleteCategory">Delete</vs-button>
        <vs-button color="primary" @click="deleteCategoryPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>
    <!-- Add Category Popup -->
    <vs-popup :title="!isNew ? 'Edit Category' : 'Add New Category'" :active.sync="defaultalert">
      <form @submit.prevent="btnAddCategory()">
        <!-- language filter -->
        <div class="mb-2 px-2 flex justify-between">
          <div class="w-1/4">
            <label class="block text-sm font-semibold mb-2">Language</label>
            <vs-select v-model="cat.language" class="w-full" :color="'#6366f1'" :disabled="showLoader">
              <vs-select-item value="" text="Select Language" disabled />
              <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
            </vs-select>
          </div>
        </div>
        <div class="px-2">
          <label class="block text-sm font-semibold mb-2">Name</label>
          <vs-input v-model="cat.categoryName" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
          <label class="block text-sm font-semibold mb-2">Short Name</label>
          <vs-input v-model="cat.shortName" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
          <div class="mb-6 flex flex-col md:flex-row md:items-center md:gap-6">
            <div class="flex-1" style="width: 100px; height: 150px">
              <div class="flex justify-between items-start">
                <label class="block text-sm font-semibold">Image</label>
                <button
                  v-if="displayImage"
                  type="button"
                  color="danger"
                  class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                  @click="clearImage"
                  :disabled="showLoader"
                >
                  Clear
                </button>
              </div>
              <div
                class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                style="width: 100%; height: 140px; min-height: 140px"
                @dragover.prevent
                @dragenter.prevent
                @drop.prevent="onDropImage"
                @click="triggerFileInput"
              >
                <img v-if="displayImage" :src="displayImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
                <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                  <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                  <span>Drag & Drop or Click to Upload</span>
                </div>
                <input
                  type="file"
                  ref="fileInput"
                  class="hidden"
                  @change="onImageChange"
                  accept="image/jpeg,image/jpg,image/webp"
                  :disabled="showLoader"
                />
              </div>
            </div>
          </div>
          <div class="flex-1">
            <vs-checkbox class="pt-2" v-model="cat.status" :disabled="showLoader">Status (Active / Inactive)</vs-checkbox>
          </div>
          <!-- Loader message -->
          <div v-if="showLoader" class="mb-4 text-blue-700">Translations are working in background...</div>

          <div class="flex flex-col md:flex-row gap-2 mt-8">
            <vs-button :loading="showLoader" color="primary" type="filled" class="flex-1" @click="btnAddCategory" :disabled="showLoader">
              {{ isNew ? "Save" : "Update" }}
            </vs-button>
          </div>
        </div>
      </form>
    </vs-popup>
  </div>
</template>

<script>
import api from "@/rest.js";
import { API } from "@/common/constants";

export default {
  name: "EventCategory",
  data: () => ({
    defaultalert: false,
    deleteCategoryPopup: false,
    isNew: true,
    clone: false,
    displayImage: "",
    fileName: "",
    search: {
      sort: "ASC",
      language: "EN",
      status: "0",
    },
    cat: {
      status: true,
      language: "",
      shortName: "",
      categoryName: "",
      image: "",
    },
    itemToDelete: "",
    itemToUpdate: "",
    all: [],
    languages: [],
    showLoader: false,
  }),
  methods: {
    addingNewItem() {
      this.$data.defaultalert = true;
      this.$data.isNew = true;
      this.$data.cat = {
        status: true,
        language: "",
        shortName: "",
        categoryName: "",
        image: "",
      };
      this.$data.displayImage = "";
    },

    editingItem(i, data) {
      this.$data.cat = Object.assign({}, i);
      this.$data.defaultalert = true;
      this.$data.isNew = false;
      this.$data.displayImage = i.image;
      this.$data.clone = data == "clone" ? true : false;
    },

    onImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.cat.image = file;
        this.displayImage = URL.createObjectURL(file);
      }
    },

    clearImage() {
      this.displayImage = "";
      this.cat.image = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },

    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    async resetData() {
      this.$data.search = {
        sort: "ASC",
        language: "EN",
        status: "0",
      };
      await this.searchData();
    },

    async btnAddCategory() {
      let apiToCall = API.INSERT_EVENT_CAT;
      let data = this.$data.cat;

      if (!data.categoryName || !data.shortName || !data.language || data.language === "all" || !data.image) {
        this.$vs.notify({
          title: "Error",
          text: "Please enter all the fields",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      this.$vs.loading();

      if (!this.$data.isNew) {
        if (this.cat.image instanceof File) await this.uploadImage({ target: { files: [this.$data.cat.image] } });
        this.$data.cat.image = this.$data.displayImage;

        apiToCall = API.UPDATE_EVENT_CAT;
        data["image"] = this.$data.displayImage;
        try {
          const r = await api.put(apiToCall + "?id=" + data._id, data);

          if (r.status === 200) {
            this.$vs.notify({
              text: "Category updated.",
              color: "success",
              position: "top-right",
            });
          }
        } catch (error) {
          this.$vs.notify({
            title: "Error",
            text: error.response.data.error,
            color: "danger",
            position: "top-right",
          });
          throw error;
        }
      } else {
        try {
          if (this.cat.image instanceof File) await this.uploadImage({ target: { files: [this.$data.cat.image] } });
          this.$data.cat.image = this.$data.displayImage;

          const r = await api.post(apiToCall, data);

          if (r.status === 200) {
            this.$vs.notify({
              text: "Category saved.",
              color: "success",
              position: "top-right",
            });
          }
        } catch (error) {
          this.$vs.notify({
            title: "Error",
            text: error.response.data.error,
            color: "danger",
            position: "top-right",
          });
          throw error;
        }
      }

      await this.searchData();
      this.$data.defaultalert = false;
      this.$vs.loading.close();
    },

    async deleteCategory() {
      this.$vs.loading();
      try {
        const r = await api.delete(API.DELETE_EVENT_CAT + "?id=" + this.$data.itemToDelete._id, {});

        if (r.status === 200) {
          this.$vs.notify({
            text: "Category deleted.",
            color: "success",
            position: "top-right",
          });
        }
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "Error",
          text: error.response.data.error,
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.deleteCategoryPopup = false;
        this.itemToUpdate = "";
        this.$vs.loading.close();
      }
    },

    async searchData() {
      this.$vs.loading();
      try {
        const r = await api.get(`${API.SEARCH_EVENT_CAT}?language=${this.$data.search.language}&status=${this.$data.search.status}`, {});
        if (r.status === 200) {
          this.$data.all = r.data;
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Failed to fetch event categories",
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async uploadImage(data) {
      const imageType = ["image/jpeg", "image/jpg", "image/webp"];
      let isValid = false;
      if (!data || !data.target || !data.target.files || !data.target.files[0]) return;
      isValid = imageType.indexOf(data.target.files[0].type) > -1;

      if (!isValid) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "Only JPEG,JPG,WEBP Image files formats should be supported",
          color: "danger",
          position: "top-right",
        });
        if (this.cat.image instanceof File) this.$refs.fileInput.value = null;
        return;
      }
      if (data.target.files[0].size > 500000) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "File size should be less than 500kb",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      let formdata = new FormData();
      this.$data.fileName = data.target.files[0].name;
      formdata.append("category", this.$data.cat.categoryName);
      formdata.append("file", data.target.files[0]);
      this.$vs.loading();

      try {
        const response = await api.post(API.UPLOAD_IMAGE, formdata, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data.error != null) {
          this.$vs.notify({
            title: "Uploading Error",
            text: response.data.error.message,
            color: "danger",
            position: "top-right",
          });
        } else {
          this.$vs.notify({
            text: "File Uploaded",
            color: "success",
            position: "top-right",
          });
          const data = response;
          this.$data.displayImage = response.data.files[0].url;
          this.$refs.fileInput.value = null;
          this.$emit("input", data);
        }
      } catch (error) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "Error while uploading file",
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },

  async created() {
    this.$data.search.language = "all";
    if (this.$route.query.catLang) this.$data.search.language = this.$route.query.catLang;

    await Promise.all([this.fetchAllLanguages(), this.searchData()]);
    this.fetchAllLanguages();
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
